import { apiFetch as fetch } from "../lib/security";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, m } from "framer-motion";
import { easeOut } from "./motion/tokens";
import { CheckCircle2, Clock3, Mail, MessageCircle, Send, Sparkles, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

type ChatConversation = {
  id: string;
  visitorName: string;
  visitorEmail: string;
  visitorPhone: string;
  status: "open" | "closed";
  preview?: string;
  unreadCount?: number;
  lastMessageAt: string;
  lastGuestMessageAt?: string | null;
  lastAdminMessageAt?: string | null;
  lastAdminReadAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

type ChatMessage = {
  id: string;
  conversationId: string;
  sender: "guest" | "admin" | "system";
  body: string;
  createdAt: string;
};

type ChatSettings = {
  responseTimeLabel: string;
  onlineMessage: string;
  offlineMessage: string;
  handoffPrompt: string;
  businessTimezone: string;
  requireEmail: boolean;
  isBusinessHours: boolean;
  businessHoursSummary: string;
  updatedAt?: string;
};

type ChatFaq = {
  id: string;
  question: string;
  answer: string;
  keywords: string[];
  enabled: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
};

type ChatConfig = {
  settings: ChatSettings;
  faqs: ChatFaq[];
};

type StoredChat = {
  id: string;
  token: string;
};

const chatStorageKey = "depositSeedHouse.chat";

const fallbackChatConfig: ChatConfig = {
  settings: {
    responseTimeLabel: "We usually answer within 15-30 minutes during business hours.",
    onlineMessage: "We are inside normal chat hours. If we miss you, you can leave the page and we will reply by email.",
    offlineMessage: "We are outside normal chat hours right now. Leave your message and we will reply when we are back, usually by email.",
    handoffPrompt: "Choose a quick answer below or send a message to reach the owner.",
    businessTimezone: "America/New_York",
    requireEmail: true,
    isBusinessHours: false,
    businessHoursSummary: "Monday-Friday 9:00 AM-5:00 PM",
  },
  faqs: [
    {
      id: "checkout",
      question: "When can I check out?",
      answer: "Check-out is at 11:00 AM. If you need extra time, message us and we will confirm whether late check-out is available.",
      keywords: ["checkout", "check out", "late checkout"],
      enabled: true,
      sortOrder: 10,
    },
  ],
};

function storedChat(): StoredChat | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const value = window.localStorage.getItem(chatStorageKey);
    return value ? (JSON.parse(value) as StoredChat) : null;
  } catch {
    return null;
  }
}

function saveStoredChat(chat: StoredChat | null) {
  if (typeof window === "undefined") {
    return;
  }

  if (!chat) {
    window.localStorage.removeItem(chatStorageKey);
    return;
  }

  window.localStorage.setItem(chatStorageKey, JSON.stringify(chat));
}

async function readJson(response: Response) {
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.error?.message ?? "Request failed");
  }

  return data;
}

function mergeMessages(current: ChatMessage[], incoming: ChatMessage[]) {
  const byId = new Map(current.map((message) => [message.id, message]));

  for (const message of incoming) {
    byId.set(message.id, message);
  }

  return Array.from(byId.values()).sort((left, right) => new Date(left.createdAt).valueOf() - new Date(right.createdAt).valueOf());
}

function parseChatEvent(event: Event) {
  try {
    return JSON.parse((event as MessageEvent).data) as {
      conversation?: ChatConversation;
      message?: ChatMessage;
      messages?: ChatMessage[];
    };
  } catch {
    return null;
  }
}

function senderLabel(message: ChatMessage) {
  if (message.sender === "admin") {
    return "Deposit Seed House";
  }

  if (message.sender === "system") {
    return "Instant answer";
  }

  return "You";
}

export function ChatWidget() {
  const { account, sessionToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState<ChatConfig | null>(null);
  const [stored, setStored] = useState<StoredChat | null>(() => storedChat());
  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [initialMessage, setInitialMessage] = useState("");
  const [selectedFaqId, setSelectedFaqId] = useState("");
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const streamRef = useRef<EventSource | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const hasConversation = Boolean(conversation && stored);
  const settings = config?.settings ?? fallbackChatConfig.settings;
  const faqs = config?.faqs ?? fallbackChatConfig.faqs;
  const selectedFaq = faqs.find((faq) => faq.id === selectedFaqId) ?? null;
  const shownFaqs = faqs.slice(0, 6);
  const isEmailRequired = !account && settings.requireEmail;
  const serviceMessage = settings.isBusinessHours ? settings.onlineMessage : settings.offlineMessage;

  const buttonLabel = useMemo(() => {
    if (!conversation) {
      return "Help / chat";
    }

    return conversation.status === "closed" ? "Start another message" : "Open chat";
  }, [conversation]);

  const authHeaders = () => {
    const headers = new Headers({ "Content-Type": "application/json" });

    if (account && sessionToken) {
      headers.set("Authorization", `Bearer ${sessionToken}`);
    }

    return headers;
  };

  const loadConversation = async (chat: StoredChat) => {
    const data = await fetch(`/api/chat/conversations/${chat.id}?token=${encodeURIComponent(chat.token)}`).then(readJson);
    setConversation(data.conversation);
    setMessages(data.messages ?? []);
  };

  useEffect(() => {
    let ignored = false;

    fetch("/api/chat/config")
      .then(readJson)
      .then((data) => {
        if (!ignored) {
          setConfig({
            settings: data.settings ?? fallbackChatConfig.settings,
            faqs: Array.isArray(data.faqs) ? data.faqs : fallbackChatConfig.faqs,
          });
        }
      })
      .catch(() => {
        if (!ignored) {
          setConfig(fallbackChatConfig);
        }
      });

    return () => {
      ignored = true;
    };
  }, []);

  useEffect(() => {
    if (!account || conversation) {
      return;
    }

    setName((current) => current || account.name);
    setEmail((current) => current || account.email);
    setPhone((current) => current || account.phone);
  }, [account, conversation]);

  useEffect(() => {
    if (!stored) {
      return;
    }

    loadConversation(stored).catch(() => {
      saveStoredChat(null);
      setStored(null);
      setConversation(null);
      setMessages([]);
    });
  }, [stored?.id, stored?.token]);

  useEffect(() => {
    if (!stored || !conversation) {
      return undefined;
    }

    streamRef.current?.close();
    const stream = new EventSource(`/api/chat/conversations/${conversation.id}/events?token=${encodeURIComponent(stored.token)}`);
    streamRef.current = stream;

    stream.addEventListener("chat-message", (event) => {
      const data = parseChatEvent(event);

      if (!data) {
        return;
      }

      if (data.conversation) {
        setConversation(data.conversation);
      }

      const message = data.message ?? null;
      const incomingMessages = data.messages ?? [];

      if (message) {
        setMessages((current) => mergeMessages(current, [message]));
      }

      if (incomingMessages.length > 0) {
        setMessages((current) => mergeMessages(current, incomingMessages));
      }
    });

    stream.addEventListener("chat-status", (event) => {
      const data = parseChatEvent(event);

      if (data?.conversation) {
        setConversation(data.conversation);
      }
    });

    stream.onerror = () => {
      stream.close();
    };

    return () => {
      stream.close();
    };
  }, [conversation?.id, stored?.token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, open]);

  const onStart = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");

    try {
      const data = await fetch("/api/chat/conversations", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          name: account?.name ?? name,
          email: account?.email ?? email,
          phone: account?.phone ?? phone,
          message: initialMessage,
        }),
      }).then(readJson);
      const nextStored = { id: data.conversation.id, token: data.token };

      saveStoredChat(nextStored);
      setStored(nextStored);
      setConversation(data.conversation);
      setMessages(data.messages ?? []);
      setDraft("");
      setInitialMessage("");
      setSelectedFaqId("");
      setStatus("Message sent. You can leave this page and we will reply by email if we miss you.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Unable to send message.");
    } finally {
      setSubmitting(false);
    }
  };

  const onSend = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stored || !conversation || !draft.trim()) {
      return;
    }

    setSubmitting(true);
    setStatus("");

    try {
      const data = await fetch(`/api/chat/conversations/${conversation.id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: stored.token, message: draft }),
      }).then(readJson);

      setConversation(data.conversation);
      setMessages(data.messages ?? mergeMessages(messages, [data.message]));
      setDraft("");
      setStatus("Message sent. You can leave this page and we will reply by email if we miss you.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Unable to send message.");
    } finally {
      setSubmitting(false);
    }
  };

  const chooseFaq = (faq: ChatFaq) => {
    setSelectedFaqId(faq.id);
    setInitialMessage((current) => current || faq.question);
  };

  return (
    <div className={`chat-widget ${open ? "chat-widget--open" : ""}`}>
      <AnimatePresence>
        {open && (
          <m.section
            className="chat-panel"
            aria-label="Guest chat"
            initial={{ opacity: 0, y: 14, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.26, ease: easeOut }}
            style={{ transformOrigin: "bottom right" }}
          >
            <div className="chat-panel__header">
            <div>
              <p className="eyebrow">Guest care</p>
              <h2>{hasConversation ? "Messages." : "How can we help?"}</h2>
            </div>
            <button className="icon-button" type="button" onClick={() => setOpen(false)} aria-label="Close chat">
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          {hasConversation ? (
            <>
              <div className="chat-conversation-note">
                <Mail size={17} aria-hidden="true" />
                <span>You can leave after sending. If we miss you, we will reply by email{conversation?.visitorEmail ? ` at ${conversation.visitorEmail}` : ""}.</span>
              </div>
              <div className="chat-thread" aria-live="polite">
                {messages.map((message) => (
                  <div className={`chat-bubble chat-bubble--${message.sender}`} key={message.id}>
                    <span>{senderLabel(message)}</span>
                    <p>{message.body}</p>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              {conversation?.status === "closed" && <p className="chat-closed-note">Conversation closed. Send a new note if you need anything else.</p>}
              <form className="chat-compose" onSubmit={onSend}>
                <label>
                  <span>Message</span>
                  <textarea rows={3} maxLength={1800} value={draft} onChange={(event) => setDraft(event.target.value)} required />
                </label>
                <button className="button button--primary button--full" type="submit" disabled={submitting || !draft.trim()}>
                  <Send size={17} aria-hidden="true" />
                  {submitting ? "Sending" : "Send message"}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="chat-assurance">
                <div className="chat-assurance__status">
                  <span className={settings.isBusinessHours ? "chat-presence chat-presence--online" : "chat-presence"} aria-hidden="true" />
                  <strong>{settings.isBusinessHours ? "Business hours now" : "After hours"}</strong>
                </div>
                <p>{serviceMessage}</p>
                <small>
                  <Clock3 size={14} aria-hidden="true" />
                  {settings.responseTimeLabel}
                </small>
                {settings.businessHoursSummary && <em>Hours: {settings.businessHoursSummary}</em>}
              </div>

              {shownFaqs.length > 0 && (
                <div className="chat-quick-help" aria-label="Quick answers">
                  <p>{settings.handoffPrompt}</p>
                  <div className="chat-quick-actions">
                    {shownFaqs.map((faq) => (
                      <button className={`chat-faq-button ${selectedFaqId === faq.id ? "chat-faq-button--active" : ""}`} type="button" key={faq.id} onClick={() => chooseFaq(faq)}>
                        <Sparkles size={14} aria-hidden="true" />
                        {faq.question}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedFaq && (
                <div className="chat-answer-card" role="status">
                  <span>Instant answer</span>
                  <strong>{selectedFaq.question}</strong>
                  <p>{selectedFaq.answer}</p>
                  <button className="text-link" type="button" onClick={() => setInitialMessage(selectedFaq.question)}>
                    Ask the owner about this
                  </button>
                </div>
              )}

              <form className="chat-start-form" onSubmit={onStart}>
                {account ? (
                  <div className="chat-account-chip">
                    <CheckCircle2 size={17} aria-hidden="true" />
                    <span>Signed in as {account.email}</span>
                  </div>
                ) : (
                  <>
                    <label>
                      <span>Name</span>
                      <input maxLength={120} value={name} onChange={(event) => setName(event.target.value)} placeholder="Name" />
                    </label>
                    <label>
                      <span>Email{isEmailRequired ? " required" : ""}</span>
                      <input type="email" maxLength={254} value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" required={isEmailRequired} />
                    </label>
                    <label className="chat-start-form__full">
                      <span>Phone optional</span>
                      <input type="tel" maxLength={40} value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="Phone" />
                    </label>
                  </>
                )}
                <label className="chat-start-form__full">
                  <span>Message</span>
                  <textarea rows={4} maxLength={1800} value={initialMessage} onChange={(event) => setInitialMessage(event.target.value)} placeholder="How can we help?" required />
                </label>
                <button className="button button--primary button--full" type="submit" disabled={submitting || !initialMessage.trim()}>
                  <Send size={17} aria-hidden="true" />
                  {submitting ? "Sending" : "Send message"}
                </button>
              </form>
            </>
          )}

            {status && <div className="chat-status">{status}</div>}
          </m.section>
        )}
      </AnimatePresence>
      <button className="chat-launcher" type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-label={buttonLabel}>
        <MessageCircle size={20} aria-hidden="true" />
        {buttonLabel}
      </button>
    </div>
  );
}
